import { BaseResource } from './base';
import {
  WhatsAppTemplate,
  CreateTemplateDto,
  UpdateTemplateDto,
  SearchTemplatesDto,
  PreviewTemplateResponse,
} from '../models/whatsapp';
import { RequestOptions, PaginatedResponseDto } from '../types';

// Import TemplateCode type from shared (re-exported via types)
type TemplateCode = string;

export class WhatsAppTemplatesResource extends BaseResource {
  private basePath = 'whatsapp/templates';

  /**
   * Create a new WhatsApp template
   */
  async create(data: CreateTemplateDto, options?: RequestOptions): Promise<WhatsAppTemplate> {
    return this.client.post<WhatsAppTemplate>(this.basePath, data, options);
  }

  /**
   * Get all WhatsApp templates with pagination and filters
   */
  async findAll(
    params?: SearchTemplatesDto,
    options?: RequestOptions,
  ): Promise<PaginatedResponseDto<WhatsAppTemplate>> {
    return this.paginate<WhatsAppTemplate>(this.basePath, params, options);
  }

  /**
   * Get a specific WhatsApp template by ID
   */
  async findOne(id: string, options?: RequestOptions): Promise<WhatsAppTemplate> {
    return this.client.get<WhatsAppTemplate>(`${this.basePath}/${id}`, undefined, options);
  }

  /**
   * Get a WhatsApp template by code
   */
  async findByCode(code: TemplateCode, options?: RequestOptions): Promise<WhatsAppTemplate> {
    return this.client.get<WhatsAppTemplate>(`${this.basePath}/code/${code}`, undefined, options);
  }

  /**
   * Update a WhatsApp template
   */
  async update(
    id: string,
    data: UpdateTemplateDto,
    options?: RequestOptions,
  ): Promise<WhatsAppTemplate> {
    return this.client.put<WhatsAppTemplate>(`${this.basePath}/${id}`, data, options);
  }

  /**
   * Delete a WhatsApp template
   */
  async remove(id: string, options?: RequestOptions): Promise<void> {
    return this.client.delete<void>(`${this.basePath}/${id}`, options);
  }

  /**
   * Preview a template with variables
   */
  async preview(
    id: string,
    variables: Record<string, any>,
    options?: RequestOptions,
  ): Promise<PreviewTemplateResponse> {
    return this.client.post<PreviewTemplateResponse>(
      `${this.basePath}/${id}/preview`,
      variables,
      options,
    );
  }
}
